import {
  AppSyncFarm,
  AppSyncFarmToken,
  MonitorVaults,
} from 'common/models/farm';
import { Nullable, Optional } from 'common/models/optional';
import { FarmUserInfoToken, SelectableToken } from 'common/models/token';
import { AppSyncUserInfo } from 'common/models/userInfo';
import { ChainId, tokenSrc } from 'constants/constants';
import { NumberUtils } from 'utils/numberUtils';
import { TokenUtils } from './tokenUtils';

export namespace FarmUtils {
  export function nonIchiTokenAddress(
    token0Address: string,
    token1Address: string,
    chainId: number
  ): string {
    if (TokenUtils.isIchiToken(chainId, token0Address)) {
      return token1Address;
    }

    return token0Address;
  }

  export function nonIchiToken(
    token0: AppSyncFarmToken,
    token1: AppSyncFarmToken,
    chainId: number
  ): AppSyncFarmToken {
    if (TokenUtils.isIchiToken(chainId, token0.address)) {
      return token1;
    }

    return token0;
  }

  export function nonIchiTokenFromFarm(farm: AppSyncFarm): AppSyncFarmToken {
    if (TokenUtils.isIchiToken(farm.chainId, farm.tokens[0].address)) {
      return farm.tokens[1];
    }

    return farm.tokens[0];
  }

  export function getMonitorVaultByAddress(
    monitorVaults: MonitorVaults[],
    address: string
  ): MonitorVaults | undefined {
    const monitorVault = monitorVaults.find(
      (v) => v.address?.toLocaleLowerCase() === address.toLocaleLowerCase()
    );

    return monitorVault;
  }

  export function getIrrByVaultAddress(
    monitorVaults: MonitorVaults[],
    address: string
  ): number | undefined {
    const monitorVault = monitorVaults.find(
      (v) => v.address?.toLocaleLowerCase() === address.toLocaleLowerCase()
    );

    return monitorVault ? monitorVault.vaultIrrAllTx : 0;
  }

  export function getFeeAprByVaultAddress(
    monitorVaults: MonitorVaults[],
    address: string
  ): number | undefined {
    const monitorVault = monitorVaults.find(
      (v) => v.address?.toLocaleLowerCase() === address.toLocaleLowerCase()
    );

    return monitorVault && monitorVault.vaultMetrics && monitorVault.vaultMetrics.length > 0 ? monitorVault.vaultMetrics[0].feeApr : 0;
  }

  export function chooseStableToken(farm: AppSyncFarm): AppSyncFarmToken {
    if (TokenUtils.isStableCoin(farm.chainId, farm.tokens[0])) {
      return farm.tokens[0];
    }

    return farm.tokens[1];
  }

  export function nonIchiTokenFromUserInfo(
    token0: FarmUserInfoToken,
    token1: FarmUserInfoToken,
    chainId: number
  ): FarmUserInfoToken {
    if (TokenUtils.isIchiToken(chainId, token0.address)) {
      return token1;
    }

    return token0;
  }

  export function getAdjustedPoolId(poolId: bigint, chainId: number): bigint {
    if (poolId >= 1000n) {
      if (chainId === ChainId.Mainnet) {
        return poolId - 1000n;
      }
      // Polygon
      if (chainId === ChainId.Mumbai) {
        return poolId - 6000n;
      }
      if (chainId === ChainId.Polygon) {
        return poolId - 4000n;
      }
    }
    // Default Ethereum
    return poolId;
  }

  export function userIchiRewards(
    userInfo: AppSyncUserInfo,
    farm: AppSyncFarm
  ): Nullable<number> {
    if (!userInfo || !userInfo.farms) {
      return null;
    }

    let decimals = 9;

    for (let i = 0; i < userInfo.farms.length; i++) {
      // temp workaround for reward decimals on polygon. TODO: do it right
      if (userInfo.farms[i].poolId >= 4000 && userInfo.farms[i].poolId < 5000) {
        decimals = 18;
      }
      if (userInfo.farms[i].poolId === farm.poolId) {
        if (farm.isGeneric) {
          decimals = farm.farm.farmRewardTokenDecimals;
        }
        return NumberUtils.BNtoNumberWithoutDecimals(
          userInfo.farms[i].rewardLP,
          decimals
        );
      }
    }
    return 0;
  }

  export function userBalance(userInfo: AppSyncUserInfo, poolId: number) {
    if (!userInfo || !userInfo.farms) {
      return 0;
    }

    for (let i = 0; i < userInfo.farms.length; i++) {
      if (userInfo.farms[i].poolId === poolId) {
        return NumberUtils.BNtoNumberWithoutDecimals(
          userInfo.farms[i].userLP,
          18
        );
      }
    }
    return 0;
  }

  export function getBaseToken(farm: Nullable<AppSyncFarm>): AppSyncFarmToken {
    if (!farm) {
      throw new Error('Could not get base token, no farm given!');
    }
    // exception for Mumbai (which hasn't added to sdk)
    const existsBaseToken =
      farm.tokens[0].isBaseToken || farm.tokens[1].isBaseToken;
    const baseToken = existsBaseToken
      ? farm.tokens[0].isBaseToken
        ? farm.tokens[0]
        : farm.tokens[1]
      : farm.tokens[0].isOneToken
      ? farm.tokens[0]
      : farm.tokens[1];
    return baseToken;
  }

  export function getBaseTokenDecimals(farm: AppSyncFarm): number {
    return getBaseToken(farm).decimals;
  }

  export function getRewardToken(
    farm: Nullable<AppSyncFarm>
  ): AppSyncFarmToken {
    if (!farm) {
      throw new Error('Could not get base token, no farm given!');
    }
    return farm.tokens[1] === getBaseToken(farm)
      ? farm.tokens[0]
      : farm.tokens[1];
  }

  export function isHodlVault(farm: Nullable<AppSyncFarm>): boolean {
    // workaround that marks all ICHI and ETH vaults as HODL vaults
    // TODO this needs to be rewritten - or probably removed completely
    return (
      getRewardToken(farm).displayName === 'ICHI' ||
      getBaseToken(farm).displayName === 'ICHI' ||
      getRewardToken(farm).displayName.toLowerCase() === 'weth' ||
      getBaseToken(farm).displayName.toLowerCase() === 'weth'
    );
  }

  export function amountInTokens(
    amountUSD: number | undefined,
    token: AppSyncFarmToken | undefined
  ) {
    if (amountUSD !== undefined && token !== undefined && token.price > 0)
      return amountUSD / token.price;
    return undefined;
  }

  export function tokenIconSrc(tokenName: Optional<string>): string {
    const iconSrc =
      tokenName === 'weenus' ||
      tokenName?.includes('_oti') ||
      tokenName === 'oti' ||
      tokenName === 'Token6'
        ? `${tokenSrc}general_test.png`
        : `${tokenSrc}logo_256_${tokenName?.toLocaleLowerCase()}.svg?4`;
    return iconSrc;
  }

  export function getTotalBaseTokenValueByScarceToken(
    farms: AppSyncFarm[],
    tokenAddress: string
  ): number {
    const tvlSum = farms
      .filter(
        (f) =>
          getRewardToken(f).address.toLocaleLowerCase() ===
          tokenAddress.toLocaleLowerCase()
      )
      .reduce(
        (fprev, fcurr) =>
          fprev + getBaseToken(fcurr).reserve * getBaseToken(fcurr).price,
        0
      );

    return tvlSum;
  }

  export function getLpPrice(farm: Nullable<AppSyncFarm>): number {
    let lpPrice = 0;
    if (farm && Number(farm.totalPoolLP) > 0) {
      // eslint-disable-next-line no-mixed-operators
      lpPrice = (Number(farm.tvl) * 10 ** 18) / Number(farm.totalPoolLP);
    }
    return lpPrice;
  }

  export function getSelectableTokenByAddress(
    farms: AppSyncFarm[],
    tokenAddress: string
  ): SelectableToken {
    const token = FarmUtils.getBaseToken(
      farms.filter((f) => FarmUtils.getBaseToken(f).address === tokenAddress)[0]
    );
    return {
      name: token.name,
      displayName: token.displayName,
      fullName: token.fullName,
      address: token.address,
      price: token.price,
      decimals: token.decimals,
    };
  }

  // get deposit token by address
  export function getAppSyncFarmTokenByAddress(
    farms: AppSyncFarm[],
    tokenAddress: string | undefined
  ): AppSyncFarmToken | undefined {
    if (!tokenAddress) {
      return undefined;
    }
    const farmsByTokenAddress = farms.filter(
      (f) =>
        FarmUtils.getBaseToken(f).address.toLowerCase() ===
        tokenAddress.toLowerCase()
    );
    if (farmsByTokenAddress.length === 0) {
      return undefined;
    } else {
      return FarmUtils.getBaseToken(farmsByTokenAddress[0]);
    }
  }

  export function getVaultByTokens(
    farms: AppSyncFarm[],
    depositToken: string | undefined,
    pairedToken: string | undefined
  ): AppSyncFarm | undefined {
    if (!depositToken || !pairedToken) {
      return undefined;
    }
    const vaults = farms.filter(
      (f) =>
        FarmUtils.getBaseToken(f).address.toLowerCase() === depositToken &&
        FarmUtils.getRewardToken(f).address.toLowerCase() === pairedToken
    );
    return vaults.length === 1 ? vaults[0] : undefined;
  }

  // get paired token by address
  export function getPairedTokenByAddress(
    farms: AppSyncFarm[],
    tokenAddress: string | undefined,
    pairedTokenAddress: string | undefined
  ): AppSyncFarmToken | undefined {
    if (!tokenAddress || !pairedTokenAddress) {
      return undefined;
    }
    const vault = getVaultByTokens(farms, tokenAddress, pairedTokenAddress);
    if (!vault) {
      return undefined;
    }
    return FarmUtils.getRewardToken(vault);
  }

  export function isPancakeVault(farm: AppSyncFarm, monitorVaults: MonitorVaults[]): boolean {
    const vault = FarmUtils.getMonitorVaultByAddress(
      monitorVaults,
      farm.lpAddress
    );
    return vault?.displayName?.includes('(bsc)') || false;
  };
}
